
import { defineComponent, ref, onMounted, watch, nextTick } from 'vue';
import { useRoute } from 'vue-router';
import axios, { AxiosError } from 'axios';
import useAlert from "@/composables/Alert"
import router from '@/router';
import { campHandleEveryoneIsTrue, campHandleEmailVerification, campHandleCNPJ, campHandleBrPhoneNumber } from '@/composables/DataValidation';
import {
  CampHeader,
  CampFormHeader,
  CampFormRackSubmitBtn,
} from '@/components';
import { useLoaderStore} from "@/store/LoaderStore"

import { IStoreData, IProfile, TInitApprovalOptions, initApprovalOptions, UF_LIST } from './PostPage.vue';

interface ICompanyResponse {
  data: {
    error: boolean,
    message: string,
    data: IStoreData
    errorCode: string
  },
  status: number
}

function checkEmptyFields(obj: IStoreData): boolean {
  for (let key in obj) {
    if (obj[key] === "") {
      return true;
    }
  }
  return false;
}

export default defineComponent({
  name: "StorePut",
  components: {
    CampHeader,
    CampFormHeader,
    CampFormRackSubmitBtn,
  },
  setup() {
    /**
     * NOTA:
     * 
     * O valor da constante booleana, "IS_PERCENTAGE_POINT_STORE_MODERATOR", deve ter o mesmo valor da propriedade
     * "IS_PERCENTAGE_POINT_STORE_MODERATOR" definida na API, disponível em "pontuei-api/app/Models/Sale/Sale.php"
     */
    const IS_PERCENTAGE_POINT_STORE_MODERATOR = false;

    const route = useRoute();
    
    const queryObject = ref<{
      isLoading: boolean,
      noError: boolean,
      data:  null | IStoreData
    }>({
      isLoading: false,
      noError: false,
      data: null
    })
    const profileField = ref<IProfile[] | null>(null)
    const groupField = ref<any[] | null>(null)
    const consultantField = ref<IProfile[] | null>(null)
    /** Consultor vinculado quando a tela foi aberta: trocar por outro exige confirmação */
    const initialConsultantId = ref<string | null>(null)
    const approvalOptions = ref<TInitApprovalOptions>([...initApprovalOptions])
    const isMainCNPJGroup = ref<boolean>(false)
    const isInitialLoad = ref<boolean>(true)  

    const useStore = useLoaderStore()
    
    onMounted(async () => {
      useStore.open()
      try {
        const [result1, result2, result3, result4] = await Promise.all([
          axios.get(`/api/getStoreView/${id}`),
          axios.get(`/api/getProfile`),
          axios.get(`/api/getGroup`),
          axios.get(`/api/getConsultantOptionList`),
        ])
        const { data } = result1
        console.log('id_profile:', data.data.id_profile, typeof data.data.id_profile)
        console.log('profileField:', result2.data.data)
        profileField.value = [{ id: "", name: "" }, ...result2.data.data]
        groupField.value = [{ id: "", name: "" }, ...result3.data.data]
        consultantField.value = result4.data.data
        if(!data.error) {
          if(typeof data?.data?.reward_the_moderator === "number")
            data.data.reward_the_moderator = !!data.data.reward_the_moderator
          if (typeof data?.data?.loginPdv === "boolean") {
            data.data.login_pdv = data.data.loginPdv
          } else if (data?.data?.login_pdv !== undefined) {
            data.data.login_pdv = !!Number(data.data.login_pdv)
          }
          if(data?.data?.rua !== undefined) {
            data.data.street = data.data.rua
          }
          if(data?.data?.numero !== undefined) {
            data.data.number = data.data.numero
          }
          if(data?.data?.bairro !== undefined) {
            data.data.neighborhood = data.data.bairro
          }
          queryObject.value.data = data.data
          initialConsultantId.value = data.data.id_consultant || null
          if(queryObject.value.data?.uf && queryObject.value.data.uf.toUpperCase() === "UF")
            queryObject.value.data.uf = ""
          groupField.value.forEach(group => {
            if(group.main_cnpj === queryObject.value.data?.cnpj) {
              isMainCNPJGroup.value = true
            }
          })
        }
        await nextTick()
        isInitialLoad.value = false
      } catch (error) {
        return router.push("/404")
      } finally {
        useStore.close()
      }
    })

    const { id } = route.params;
    const { showTimeAlert, showTimeConfirmAction } = useAlert()

    /** Warning of unfilled fields */
    const isRequiredField = ref(false)
    const isInvalidFieldWarning = ref(false)
    const isInvalidField = ref<{ 
      cnpj: boolean,
      mail: boolean,
      phoneNumber: boolean
     }>({
      cnpj: false,
      mail: false,
      phoneNumber: false
    })

    /** Validate CNPJ */
    watch(() => queryObject.value.data?.cnpj, () => {
      if(queryObject.value.data?.cnpj) {
        isInvalidField.value.cnpj = !campHandleCNPJ(queryObject.value.data.cnpj)
      }
    })

    /** Validate email */
    watch(() => queryObject.value.data?.email, () => {
      if(queryObject.value.data?.email) {
        isInvalidField.value.mail = !campHandleEmailVerification(queryObject.value.data.email)
      }
    })

    /** Validate phone number */
    watch(() => queryObject.value.data?.telephone, () => {
      if(queryObject.value.data?.telephone) {
        isInvalidField.value.phoneNumber = !campHandleBrPhoneNumber(queryObject.value.data.telephone)
      }
    })

    /** Reset reward the moderator*/
    watch(() => queryObject.value.data?.sale_status_default, () => {
      if(!queryObject.value.data)
        return
      if(queryObject.value.data.sale_status_default == 2)
        queryObject.value.data.reward_the_moderator = false
    })

    watch(()=> queryObject.value.data?.login_pdv, () => {
      if(isInitialLoad.value) return
      if(queryObject.value.data){
        if(queryObject.value.data.login_pdv) {
          queryObject.value.data.id_profile = 15
        } else {
          queryObject.value.data.id_profile = ""
        }
      }
    })

    const clearWarning = () => (isRequiredField.value = false, isInvalidFieldWarning.value = false)

    async function onSubmitForm() {
      isInvalidFieldWarning.value = false
      isRequiredField.value = false
      const { data } = queryObject.value
      if(!data) {
        return
      }
      const { id_profile, id_group, id_consultant, percent_value, code_regional_internal_salesman, reward_the_moderator, id_store, created_at, ...requiredFields } = data
      if(checkEmptyFields(requiredFields)) {
        return isRequiredField.value = true
      }
      if(campHandleEveryoneIsTrue(isInvalidField.value)) {
        isInvalidFieldWarning.value = true
        return isRequiredField.value = true
      }

      const saveStore = async () => {
        useStore.open()
        try {
          const { data } = queryObject.value
          if(data) {
            const { telephone, cnpj, ...res } = data
            const putData = {
              ...res,
              reward_the_moderator,
              telephone: campHandleBrPhoneNumber(telephone, "unformatted"),
              cnpj: campHandleCNPJ(cnpj, "unformatted"),
              id_profile: id_profile ? id_profile : null,
              id_group: id_group && !isMainCNPJGroup.value ? id_group : null,
              id_consultant: id_consultant ? id_consultant : null,
              percent_value: percent_value? parseInt(percent_value.toString().replace('%', '')) : null
            }
            const result: ICompanyResponse = await axios.put(`/api/putStore/${data.id}`, putData)
            showTimeAlert("Atualizado com sucesso!")
            return router.push("/lojas")
          }
        } catch (error) {
          if(error instanceof AxiosError) {
              showTimeAlert(error.response?.data.message, "error")
          } else {
            showTimeAlert("Algo deu errado!", "error")
          }
          useStore.close()
        }
      }

      // A loja já tem consultor e foi escolhido outro: só salva se o usuário confirmar a substituição
      if(initialConsultantId.value && id_consultant && id_consultant !== initialConsultantId.value) {
        const currentConsultant = consultantField.value?.find(el => el.id === initialConsultantId.value)
        return showTimeConfirmAction(
          "Loja já possui consultor",
          `A loja já possui o consultor ${ currentConsultant?.name ?? '' } vinculado. Deseja substituí-lo pelo novo consultor?`,
          "Substituir",
          "",
          () => saveStore(),
          "warning",
          false
        )
      }

      return saveStore()
    }

    return {
      IS_PERCENTAGE_POINT_STORE_MODERATOR,
      queryObject,
      isRequiredField,
      isInvalidField,
      isInvalidFieldWarning,
      onSubmitForm,
      profileField,
      groupField,
      consultantField,
      approvalOptions,
      clearWarning,
      isMainCNPJGroup,
      UF_LIST,
    }
  }
})
